import { useEffect, useState, CSSProperties, Fragment } from 'react';
import MaestroLogo from 'assets/images/maestro/maestro-logo.svg';
import MaestroStarScore from 'assets/images/maestro/star-score-filled.svg';
import MaestroStarScoreOutline from 'assets/images/maestro/star-score-outline.svg';
import Colors from "modules/Colors";
import Sty, { Container } from 'modules/Sty';
import { motion } from "framer-motion";
import { CampaignDetails } from 'modules/Types';
import MaestroBadge from 'components/shareable/MaestroBadge';
import useScreen from 'Hooks/useScreen';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import useBrand from 'Hooks/useBrand';
import ButtonSymphony from 'elements/ButtonSymphony';
import ModalSymphony from 'elements/ModalSymphony';
import { getCampaignMaestroRecommendations_api } from 'api/campaignApi';
import { clsx, styx } from 'modules/Utils';
import Icon from 'elements/Icon';
import UpgradeToUnlockCTA from 'pages/WebsitePage/components/CustomizeWebsite/components/UpgradeToUnlockCTA';
import { PlanType } from 'modules/Const';
import useMaestroChat from 'Hooks/useMaestroChat';
import Banner from 'elements/Banner';
import { track } from 'analytics';
import { unlockAdBoxAnalytics_api } from 'api/campaignApi';
import { toastError } from 'modules/Toast';
dayjs.extend(advancedFormat);

// Each recommendation is an object like this:
type Action = {
    title: string;
    action: 'pause_campaign' | 'increase_budget' | 'edit_end_date' | 'create_campaign' | 'download_report';
    value?: string; // e.g., 'record_streams' or '50' (for $50)
};

export const MAESTRO_STATUS = {
    LOADING: 'loading',
    PENDING: 'pending',
    READY: 'ready',
    TRAINING: 'training',
} as const;

type MaestroStatusType = typeof MAESTRO_STATUS[keyof typeof MAESTRO_STATUS];
/**
 * Interface for past recommendations
 */
interface MaestroScorecard {
    title: string;
    subtitle: string;
    score: number | null;
    starScore: number | null;
    insights: Array<{
        text: string;
        replacements: Array<{
            color: 'red' | 'green';
            text: string;
        }>;
    }>;
    mainMessage: Array<{
        text: string;
        replacements: Array<{
            color: 'red' | 'green';
            text: string;
        }>;
    }>;
    tipsToImprove: string[];
    recommendedFollowupQuestions: string[];
    generationTime?: Date;
}


interface MaestroLoadingMessageProps {
    /** Custom loading messages to display. If not provided, defaults will be used */
    loadingMessages?: string[];
    /** Additional CSS class name */
    className?: string;
    /** Whether the loading animation should be active */
    startLoading?: boolean;
    /** Status to determine if loading should continue */
    maestroStatus?: MaestroStatusType;
}

export const MaestroLoadingMessage = ({
    loadingMessages = [],
    className = '',
    startLoading = false,
    maestroStatus = MAESTRO_STATUS.LOADING,
}: MaestroLoadingMessageProps) => {
    /** Index of current loading message being displayed */
    const [loadingMessageIndex, setLoadingMessageIndex] = useState<number>(0);
    /** Index of current ellipsis state being displayed */
    const [loadingMessageEllipsisIndex, setLoadingMessageEllipsisIndex] = useState<number>(0);

    const LOADING_MESSAGES = loadingMessages.length ? loadingMessages : [
        'Analyzing your campaign',
        'Generating insights',
        'Analyzing your campaign performance',
        'Evaluating your marketing strategy',
        'Reviewing campaign metrics',
        'Generating recommendations',
        'Crunching the numbers',
        'Identifying optimization opportunities',
        'Processing campaign data',
        'Calculating performance metrics',
        'Building your personalized report'
    ]

    const LOADING_MESSAGES_ELLIPSIS = [
        '.',
        '..',
        '...',
        '....',
        '.....',
    ]

    useEffect(() => {
        let messageInterval: NodeJS.Timeout;
        let ellipsisInterval: NodeJS.Timeout;

        if (startLoading && maestroStatus !== MAESTRO_STATUS.READY) {
            // Update loading message every 5 seconds
            messageInterval = setInterval(() => {
                setLoadingMessageIndex(prevIndex =>
                    (prevIndex + 1) % LOADING_MESSAGES.length
                );
            }, 5000);

            // Update ellipsis every 500ms
            ellipsisInterval = setInterval(() => {
                setLoadingMessageEllipsisIndex(prevIndex =>
                    (prevIndex + 1) % LOADING_MESSAGES_ELLIPSIS.length
                );
            }, 500);
        }

        return () => {
            clearInterval(messageInterval);
            clearInterval(ellipsisInterval);
        }
    }, [startLoading, maestroStatus, LOADING_MESSAGES.length]);

    if (!startLoading) {
        return null;
    }

    return (
        <motion.div
            className={clsx('loadingMessage', className)}
            style={{ fontStyle: 'italic' }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            key={loadingMessageIndex}
            transition={{
                duration: 0.4,
                ease: 'easeOut'
            }}
        >
            <motion.span>
                {LOADING_MESSAGES[loadingMessageIndex]}
            </motion.span>
            <motion.span
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{
                    duration: 1.2,
                    ease: 'easeInOut',
                    repeat: Infinity,
                }}
            >
                {LOADING_MESSAGES_ELLIPSIS[loadingMessageEllipsisIndex]}
            </motion.span>
        </motion.div>
    )
}

interface MaestroBannerProps {
    className?: string;
    style?: CSSProperties;
    campaignDetails: CampaignDetails;
    isAdBoxAnalyticsUnlocked?: boolean;
    isAdBoxCampaign?: boolean;
}

const MaestroBanner = ({
    className,
    style,
    campaignDetails,
    isAdBoxAnalyticsUnlocked,
    isAdBoxCampaign,
}: MaestroBannerProps) => {
    const { mStyle } = useScreen();
    const { isProBrand } = useBrand()
    const maestroChat = useMaestroChat();

    let generationTime = campaignDetails?.campaign_metadata?.maestro_recommendations?.generationTime || '';

    /** Campaign is in a not-running state (TO_RETRY or WITH_ISSUES) */
    const {
        v2_campaign_input
    } = campaignDetails?.campaign_metadata ?? {};

    let startDate;
    const lengthConfig = v2_campaign_input?.lengthConfig;
    if (lengthConfig && 'finalStartDate' in lengthConfig && lengthConfig.finalStartDate) {
        startDate = lengthConfig.finalStartDate
    } else {
        startDate = lengthConfig?.startDate
    }
    const campaignRunningForMoreThan48Hours = startDate && dayjs(startDate).add(48, 'hours').isAfter(dayjs())

    const isCampaignNotRunning = campaignDetails?.status === 'TO_RETRY' || campaignDetails?.status === 'WITH_ISSUES' || campaignDetails.status === 'IN_REVIEW' || campaignRunningForMoreThan48Hours

    // Check if the user has access to Maestro
    const [maestroStatus, setMaestroStatus] = useState<MaestroStatusType>(MAESTRO_STATUS.LOADING); // LOADING, TRAINING, READY
    const [maestroData, setMaestroData] = useState<MaestroScorecard | null>(null);
    const [isError, setIsError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [showMaestroScoreCardModal, setShowMaestroScoreCardModal] = useState<boolean>(false);

    // countdown value shown in UI
    const [countdown, setCountdown] = useState<string>();
    const [startMaestroLoader, setStartMaestroLoader] = useState<boolean>(false);

    // State for tracking unlock process
    const [isUnlockingAnalytics, setIsUnlockingAnalytics] = useState<boolean>(false);

    // Handler for unlocking adbox analytics
    const handleUnlockAdBoxAnalytics = async () => {
        if (isUnlockingAnalytics) return;

        try {
            setIsUnlockingAnalytics(true);
            const response = await unlockAdBoxAnalytics_api({
                campaignId: campaignDetails.id,
                source: 'maestro_banner_scorecard'
            });

            if (response.error || !response.url) {
                toastError('Unable to process unlock request. Please try again.');
                return;
            }

            // Redirect to checkout
            window.location.href = response.url;
        } catch (error) {
            console.error('Error unlocking analytics:', error);
            toastError('An error occurred. Please try again.');
        } finally {
            setIsUnlockingAnalytics(false);
        }
    };

    useEffect(() => {
        // Track that user saw the Maestro banner
        track('Maestro Banner Viewed', {
            campaign_id: campaignDetails?.id,
            campaign_name: campaignDetails?.campaign_name,
            campaign_status: campaignDetails?.status,
            is_campaign_running: !isCampaignNotRunning
        });

        // Only call the API if the campaign is in a running state
        if (!isCampaignNotRunning) {
            getMaestroData()
        }
        setStartMaestroLoader(true)
    }, []) // No dependency on isCampaignNotRunning - only run once on mount

    /**
     * Effect hook to manage countdown timer for next Maestro report
     * 
     * This effect:
     * 1. Creates a timer that updates every second
     * 2. Calculates time remaining until next report by:
     *    - Getting current time and next report time
     *    - Converting difference to hours, minutes, seconds
     * 3. Updates countdown state with formatted time string
     * 4. Cleans up timer on unmount or when countdown reaches zero
     * 
     * @dependency maestroData - Reruns when maestroData changes to reset timer
     */
    useEffect(() => {
        let timer: NodeJS.Timeout;

        /**
         * Updates the countdown display with time remaining until next report
         * Formats time as "XXh YYm ZZs" or clears if time expired
         */
        const updateCountdown = () => {
            const now = new Date();
            const nextReport = new Date(generationTime ?? '');
            // Add 24 hours to generation time
            const reportExpiry = new Date(nextReport.getTime() + (24 * 60 * 60 * 1000));
            const difference = reportExpiry.getTime() - now.getTime();

            if (difference > 0) {
                // Convert milliseconds to hours/minutes/seconds
                const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((difference % (1000 * 60)) / 1000);

                setCountdown(`${hours}h ${minutes}m ${seconds}s`);
            } else {
                // Clear countdown when time expires
                setCountdown('');
                clearInterval(timer);
            }
        };

        if (maestroData && generationTime) {
            updateCountdown(); // Initial update
            timer = setInterval(updateCountdown, 1000);
        }

        // Cleanup timer on unmount
        return () => clearInterval(timer);
    }, [maestroData]);

    const getMaestroData = async () => {
        try {
            setIsError(false);

            // Track that we're starting to fetch Maestro data
            track('Maestro Data Request Started', {
                campaign_id: campaignDetails?.id,
                campaign_name: campaignDetails?.campaign_name,
            });

            const maestroData = await getCampaignMaestroRecommendations_api({ campaignId: campaignDetails.id });

            // Check if we have valid data with all required fields
            if (maestroData && !maestroData.isError) {
                // If the API returns campaign performance data with metrics, it's ready
                if (maestroData.performanceMetrics ||
                    (maestroData.title && maestroData.score !== null && maestroData.starScore !== null)) {
                    setMaestroStatus(MAESTRO_STATUS.READY);
                    setMaestroData(maestroData);

                    // Track that scorecard is ready to view
                    track('Maestro Scorecard Ready', {
                        campaign_id: campaignDetails?.id,
                        campaign_name: campaignDetails?.campaign_name,
                        star_score: maestroData.starScore,
                        has_insights: maestroData.insights?.length > 0,
                        has_tips: maestroData.tipsToImprove?.length > 0
                    });
                } else {
                    // Use the status from the API or default to loading
                    const newStatus = maestroData.status || MAESTRO_STATUS.LOADING;
                    setMaestroStatus(newStatus);
                    setMaestroData(maestroData);

                    // Track the status change
                    track('Maestro Status Updated', {
                        campaign_id: campaignDetails?.id,
                        campaign_name: campaignDetails?.campaign_name,
                        status: newStatus
                    });
                }
            } else {
                setMaestroStatus(MAESTRO_STATUS.LOADING);
                setMaestroData(maestroData);

                // Track that data was received but is not complete
                track('Maestro Data Incomplete', {
                    campaign_id: campaignDetails?.id,
                    campaign_name: campaignDetails?.campaign_name,
                    is_error: maestroData?.isError || false
                });
            }
        } catch (error: any) {
            console.error('Error fetching Maestro data:', error);
            setErrorMessage(error.message);
            setIsError(true);

            // Track the error
            track('Maestro Data Request Error', {
                campaign_id: campaignDetails?.id,
                campaign_name: campaignDetails?.campaign_name,
                error_message: error.message
            });
        }
    }

    const getColorStyle = (color: string) => {
        switch (color) {
            case 'green':
                return Colors.green
            case 'red':
                return Colors.red
        }
    }

    const textReplacer = (text: string | undefined, replacements: { characterIndex?: number, color: string, text: string }[]) => {
        if (!text) {
            return null;
        }

        let parts = [];
        let lastIndex = 0;

        replacements.forEach(replacement => {
            const {
                characterIndex,
                color,
                text: replacementText
            } = replacement;

            const colorStyle = getColorStyle(color);

            let index = characterIndex !== undefined
                ? characterIndex
                : text.indexOf(replacementText, lastIndex);

            if (index === -1) {
                return; // Skip this replacement if the text is not found
            }

            if (index > lastIndex) {
                parts.push(text.slice(lastIndex, index));
            }
            parts.push(
                <span
                    key={index}
                    className="replacementText"
                    style={{ color: colorStyle, fontWeight: 500 }}
                >
                    {replacementText}
                </span>
            );
            lastIndex = index + replacementText.length;
        });

        if (lastIndex < text.length) {
            parts.push(text.slice(lastIndex));
        }

        return (
            <>
                {parts.map((part, index) =>
                    typeof part === 'string'
                        ? part.split('\n').map((line, lineIndex) =>
                            <Fragment key={`line-${index}-${lineIndex}`}>
                                {line}
                                {lineIndex < part.split('\n').length - 1 && <br />}
                            </Fragment>
                        )
                        : part
                )}
            </>
        );
    }

    const getGradeFromScore = (score: number) => {
        if (score === null || score === undefined) {
            return null;
        }

        if (score >= 97) {
            return 'A+';
        } else if (score >= 93) {
            return 'A';
        } else if (score >= 90) {
            return 'A-';
        } else if (score >= 87) {
            return 'B+';
        } else if (score >= 83) {
            return 'B';
        } else if (score >= 80) {
            return 'B-';
        } else if (score >= 77) {
            return 'C+';
        } else if (score >= 73) {
            return 'C';
        } else if (score >= 70) {
            return 'C-';
        } else if (score >= 67) {
            return 'D+';
        } else if (score >= 63) {
            return 'D';
        } else if (score >= 60) {
            return 'D-';
        } else {
            return 'F';
        }
    }

    const renderMaestroBadgeLine = () => {
        return (
            <>
                <Container
                    className="maestroBannerBadgeLine"
                    display="flex"
                    flexDirection="row"
                    gap="8px"
                    alignItems="center"
                    justifyContent="flex-start"
                >
                    <MaestroBadge className="maestroBadge" subtitle="Scorecard" />
                </Container>
                {countdown &&
                    <div
                        className="countdownContainer"
                        style={styx(Sty.text12grey, { marginTop: 8 })}
                    >
                        {dayjs(generationTime).add(24, 'hours').diff(dayjs(), 'hours') < 1 ?
                            `Next scorecard in ${countdown}`
                            :
                            `Next scorecard on ${dayjs(generationTime).add(24, 'hours').format('MMM Do, h:mm A')}`
                        }
                    </div>
                }
            </>
        )
    }

    const renderStatusMessage = () => {
        const {
            subtitle = null
        } = maestroData ?? {};

        if (isError) {
            return (
                <div className="errorContainer" style={sty.errorContainer}>
                    {renderMaestroBadgeLine()}

                    <div style={{ color: Colors.red }}>
                        {errorMessage}
                    </div>
                </div>
            )
        }

        switch (maestroStatus) {
            case MAESTRO_STATUS.LOADING:
                return (
                    <div className="loadingContainer">
                        {renderMaestroBadgeLine()}
                        <MaestroLoadingMessage
                            startLoading={startMaestroLoader}
                            maestroStatus={maestroStatus}
                        />
                    </div>
                )
            case MAESTRO_STATUS.TRAINING:
                return (
                    <div className="trainingContainer">
                        {renderMaestroBadgeLine()}
                        <div className="subtitle">
                            {subtitle}
                        </div>
                        <div className="loadingBarContainer" style={sty.loadingBarContainer}>
                            <div className="loadingBar" style={sty.loadingBar}>
                                <motion.div
                                    initial={{ width: '0%' }}
                                    animate={{ width: `0%` }}
                                    transition={{ duration: 1, ease: "easeOut" }}
                                    className="loadingBarProgress"
                                    style={{
                                        height: '100%',
                                        backgroundColor: 'rgba(136, 0, 255, 0.40)',
                                        borderRadius: 12,
                                    }}
                                />
                            </div>
                        </div>
                        <div style={{ color: Colors.greyDark }}>
                            We're waiting for more data to come in before we can give a good analysis. Stay tuned - we'll have a new report tomorrow.
                        </div>
                    </div>
                )
            default:
                return null
        }
    }

    const renderMaestroStars = () => {
        const {
            starScore = null
        } = maestroData ?? {};

        if (starScore === null) {
            return null;
        }

        return (
            <div className="maestroStarsContainer" style={sty.maestroStarsContainer}>
                {renderMaestroStarScore(starScore >= 1)}
                {renderMaestroStarScore(starScore >= 2)}
                {renderMaestroStarScore(starScore >= 3)}
            </div>
        )
    }

    const renderMaestroStarScore = (isFilled = false) => {
        return (
            <img
                src={isFilled ? MaestroStarScore : MaestroStarScoreOutline}
                alt="Maestro Star Score"
                style={sty.maestroStarScore}
            />
        )
    }

    const getGradeSubtitleFromStarScore = () => {
        const {
            starScore = null
        } = maestroData ?? {};

        if (starScore === null) {
            return null;
        }

        switch (starScore) {
            case 1:
                return 'Could be improved';
            case 2:
                return 'Good Campaign';
            case 3:
                return 'Above Average Campaign';
            default:
                return 'Not Rated';
        }
    }

    const renderMaestroScoreAsGrade = () => {
        const {
            starScore = null
        } = maestroData ?? {};

        if (starScore === null) {
            return null;
        }

        return (
            <div className="gradeContainer" style={sty.gradeContainer}>
                {/* <div className="grade" style={sty.grade}>
                        {getGradeFromScore(score)}
                    </div>
                    <div className="gradeSubtitle" style={sty.gradeSubtitle}>
                        Campaign Score
                    </div>
                    <ArcLoadingBar percentage={score} /> */}
                {renderMaestroStars()}
                <div
                    className="gradeSubtitle"
                    style={{
                        ...Sty.text12grey,
                        marginTop: 8,
                    }}
                >
                    {getGradeSubtitleFromStarScore()}
                </div>
            </div>
        )
    }

    const renderSideImage = () => {
        let maestroStatusBody = null;

        if (isError) {
            maestroStatusBody = (
                <div
                    className="errorSymbol"
                    style={{
                        ...Sty.text24bold,
                        display: 'flex',
                        gap: 16,
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: Colors.red,
                    }}
                >
                    ×
                </div>
            )
        } else {
            switch (maestroStatus) {
                case MAESTRO_STATUS.LOADING:
                    return (
                        <div style={{ paddingLeft: 8, paddingRight: 8 }}>
                            <Icon
                                className="maestroLogoFromMaestroBanner"
                                icon="maestroBadgeImage"
                                size={72}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            />
                        </div>
                    )
                    break
                case MAESTRO_STATUS.TRAINING:
                    maestroStatusBody = (
                        <img
                            className="maestroLogo"
                            style={sty.maestroLogo}
                            src={MaestroLogo}
                            alt="Maestro Logo"
                        />
                    )
                    break
                default:
                    return null
            }
        }

        return (
            <div
                className="maestroSideContainer"
                style={sty.maestroSideContainer}
            >
                {maestroStatusBody}
            </div>
        )
    }

    const renderMaestroInsights = () => {
        const {
            insights = null
        } = maestroData ?? {};

        if (insights === null) {
            return null;
        }

        return (
            <div className="maestroInsights" style={sty.maestroGenericContentText}>
                <div style={Sty.text16bold}>Insights</div>
                <div className="insightsRow" style={sty.insightsRow}>
                    {insights.map((insight, index) => (
                        <div key={index} className="maestroInsight" style={sty.maestroInsight}>
                            <div className="maestroInsightHeader" style={sty.maestroInsightHeader}>
                                <img src={MaestroLogo} alt="Maestro Logo" style={sty.maestroLogoInsight} />
                                <div>Insight</div>
                            </div>
                            <div>{textReplacer(insight.text, insight.replacements ?? [])}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // const renderMaestroRecommendedActions = () => {
    //     const {
    //         post_campaign_recommendations = null
    //     } = maestroData ?? {};

    //     if (post_campaign_recommendations === null) {
    //         return null;
    //     }

    //     return (
    //         <div className="maestroRecommendedActionsContainer" style={sty.maestroRecommendedActionsContainer}>
    //             <div className="sectionHeaderTitle" style={sty.sectionHeaderTitle}>Recommended Actions</div>
    //             <div className="maestroRecommendedActionsRow" style={sty.maestroRecommendedActionsRow}>
    //                 {post_campaign_recommendations.map((action, index) => (
    //                     <ButtonSymphony
    //                         key={index}
    //                     >
    //                         {action.title}
    //                     </ButtonSymphony>
    //                 ))}
    //             </div>
    //         </div>
    //     );
    // }

    const renderMainMessage = () => {
        const {
            mainMessage = null,
        } = maestroData ?? {};

        if (mainMessage === null) {
            return null;
        }

        return (
            <div className="maestroPerformanceSummary" style={sty.maestroGenericContentText}>
                <div className="maestroPerformanceSummaryTitle" style={Sty.text16bold}>
                    Performance Summary
                </div>
                {mainMessage?.map((message, index) => (
                    <div key={index} className="maestroPerformanceSummaryMessage">
                        {textReplacer(message?.text, message?.replacements ?? [])}
                    </div>
                ))}
            </div>
        )
    }

    const renderTipsToImprove = () => {
        const {
            tipsToImprove = null,
        } = maestroData ?? {};

        if (tipsToImprove === null) {
            return null;
        }

        return (
            <div className="maestroTipsToImprove" style={sty.maestroGenericContentText}>
                <div
                    className="maestroTipsToImproveTitle"
                    style={Sty.text16bold}
                >
                    Tips to Improve your Score
                </div>
                <ol
                    className="maestroTipsToImproveList"
                    style={{
                        listStyle: 'decimal',
                        paddingLeft: 20,
                    }}
                >
                    {tipsToImprove?.map((message, index) => (
                        <li key={index} className="maestroTipsToImproveItem">
                            {textReplacer(message, [])}
                        </li>
                    ))}
                </ol>
            </div>
        )
    }

    /** 
     * Handles clicking on a follow-up question by sending the question to Maestro chat
     * @param question The follow-up question text to send
     */
    const handleFollowUpQuestionClick = (question: string) => {
        if (!question) return;

        // Track that user clicked a follow-up question
        track('Maestro Follow-up Question Clicked', {
            campaign_id: campaignDetails?.id,
            campaign_name: campaignDetails?.campaign_name,
            question: question
        });

        // Create context message with summary and question
        const contextMessage = `
Context: User is viewing a Maestro campaign report with the following summary:
Title: ${maestroData?.title || ''}
Subtitle: ${maestroData?.subtitle || ''}
Campaign rating: ${maestroData?.starScore}/3 stars
The user has asked: "${question}"
        `.trim();

        // Send message and open chat
        maestroChat.sendMessage(contextMessage);
        maestroChat.setIsChatOpen(true);
    };

    /**
     * Renders recommended follow-up questions as interactive buttons
     */
    const renderFollowUpQuestions = () => {
        return null;

        const { recommendedFollowupQuestions = [] } = maestroData ?? {};

        if (!recommendedFollowupQuestions?.length) {
            return null;
        }

        return (
            <motion.div
                className="followUpQuestionsContainer"
                style={sty.followUpQuestionsContainer}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
            >
                <div style={Sty.text16bold}>Ask Maestro</div>
                <div className="followUpQuestionsList" style={sty.followUpQuestionsList}>
                    {recommendedFollowupQuestions.map((question, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.3 + (index * 0.1), duration: 0.4 }}
                            whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <ButtonSymphony
                                className="followUpQuestionButton"
                                variant="outlined"
                                onClick={() => handleFollowUpQuestionClick(question)}
                                style={sty.followUpQuestionButton}
                            >
                                {question}
                            </ButtonSymphony>
                        </motion.div>
                    ))}
                </div>
            </motion.div>
        );
    };

    const isAllowedToShowScorecard = isProBrand || isAdBoxAnalyticsUnlocked

    const renderMaestroMainContent = () => {
        if (maestroStatus !== MAESTRO_STATUS.READY) {
            return null;
        }

        // Check if this is an AdBox campaign that needs unlock
        const shouldShowAdBoxUnlock = isAdBoxCampaign && !isAdBoxAnalyticsUnlocked && !isProBrand;

        return (
            <div
                className="maestroMainContentReady"
                style={sty.maestroMainContentReady}
            >
                {renderMaestroBadgeLine()}
                <div
                    className="maestroTitleContainer"
                    style={mStyle(sty, 'maestroTitleContainer')}
                >
                    {renderMaestroScoreAsGrade()}
                    <div className="maestroTitleInnerContainer" style={sty.maestroTitleInnerContainer}>
                        <div className="maestroMainContentTitle" style={Sty.text16bold}>
                            {maestroData?.title}
                        </div>
                        <div className="maestroMainContentSubtitle" style={Sty.text16grey}>
                            {isAllowedToShowScorecard ? maestroData?.subtitle : 'AI has scanned your campaign and has some recommendations.'}
                        </div>
                    </div>
                </div>
                {isAllowedToShowScorecard ?
                    <ButtonSymphony
                        className="viewReportButton"
                        onClick={() => {
                            track('Maestro View Scorecard Button Clicked', {
                                campaign_id: campaignDetails?.id,
                                campaign_name: campaignDetails?.campaign_name,
                                star_score: maestroData?.starScore
                            });
                            setShowMaestroScoreCardModal(true);
                        }}
                        width="100%"
                    >
                        View Report + Recommendations
                    </ButtonSymphony>
                    :
                    shouldShowAdBoxUnlock ?
                        <ButtonSymphony
                            className="unlockAdBoxAnalyticsButton"
                            onClick={handleUnlockAdBoxAnalytics}
                            width="100%"
                            variant="primary"
                            isLoading={isUnlockingAnalytics}
                            disabled={isUnlockingAnalytics}
                        >
                            {isUnlockingAnalytics ? 'Processing...' : 'Unlock for $20'}
                        </ButtonSymphony>
                        :
                        <UpgradeToUnlockCTA
                            highlightedPlan={PlanType.PRO}
                            requiresPaidPlan
                            paywallText="Get AI-powered insights and recommendations to optimize your campaign performance"
                            buttonText="Unlock your scorecard with Symphony Pro"
                            upgradeSource="Campaign Details - Maestro Banner"
                        />
                }
            </div>
        )
    }

    /** 
     * Renders the detailed scorecard content in a modal
     */
    const renderScoreCardModal = () => {
        if (!isProBrand || !maestroData) return null;

        return (
            <ModalSymphony
                className="maestroScoreCardModal"
                isOpen={showMaestroScoreCardModal}
                onClose={() => setShowMaestroScoreCardModal(false)}
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MaestroBadge className="maestroBadge" subtitle="Scorecard" />
                    </div>
                }
                size="large"
                style={{}}
            >
                <div className="scoreCardModalContent" style={sty.scoreCardModalContent}>
                    <div className="scoreCardModalHeader" style={mStyle(sty, 'scoreCardModalHeader')}>
                        <div className="scoreCardStarsContainer" style={sty.scoreCardStarsContainer}>
                            {renderMaestroStars()}
                            <div style={styx(Sty.text12grey, { marginTop: 8 })}>
                                {getGradeSubtitleFromStarScore()}
                            </div>
                        </div>

                        <div className="scoreCardTitleContainer" style={sty.scoreCardTitleContainer}>
                            <div style={Sty.text18bold}>
                                {maestroData.title}
                            </div>
                            <div style={Sty.text16grey}>
                                {maestroData.subtitle}
                            </div>
                        </div>
                    </div>

                    <div className="scoreCardDetailsContainer" style={sty.scoreCardDetailsContainer}>
                        {renderMainMessage()}
                        {renderMaestroInsights()}
                        {renderTipsToImprove()}
                        {renderFollowUpQuestions()}

                        <ButtonSymphony
                            variant="link"
                            className="provideFeedbackButton"
                            href="mailto:megh@symphony.to?subject=Maestro%20Scorecard%20Feedback&body=I%20have%20some%20feedback%20on%20the%20Maestro%20Scorecard%20for%20campaign%20${campaignDetails?.id}%20-%20${campaignDetails?.campaign_name}%20:%0A%0A"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Have feedback on your scorecard? Let us know if you like it or not! &gt;
                        </ButtonSymphony>
                    </div>
                </div>
            </ModalSymphony>
        );
    };

    const renderUnlockReportButton = () => {
        if (isAdBoxCampaign) {
            if (isAdBoxAnalyticsUnlocked) {
                return null;
            } else {
                return (
                    <ButtonSymphony
                        className="unlockAdBoxAnalyticsButton"
                        onClick={handleUnlockAdBoxAnalytics}
                        width="100%"
                        variant="primary"
                        isLoading={isUnlockingAnalytics}
                        disabled={isUnlockingAnalytics}
                    >
                        {isUnlockingAnalytics ? 'Processing...' : 'Unlock for $20'}
                    </ButtonSymphony>
                )
            }
        } else if (isProBrand) {
            return null;
        } else {
            return (
                <UpgradeToUnlockCTA
                    highlightedPlan={PlanType.PRO}
                    requiresPaidPlan
                    paywallText="Get AI-powered insights and recommendations to optimize your campaign performance"
                    buttonText="Unlock your scorecard with Symphony Pro"
                    upgradeSource="Campaign Details - Maestro Banner"
                />
            )
        }
    }

    /** 
     * Renders the banner content for campaigns that are in TO_RETRY or WITH_ISSUES state
     * Shows an informational message about scorecards being available when campaign is live
     */
    const renderCampaignNotRunningContent = () => {
        // Check if this is an AdBox campaign that needs unlock
        const shouldShowAdBoxUnlock = isAdBoxCampaign && !isAdBoxAnalyticsUnlocked && !isProBrand;

        return (
            <div
                className="maestroLoadingTrainingContainer"
                style={sty.maestroLoadingTrainingContainer}
            >
                <div style={{
                    paddingLeft: 8,
                    paddingRight: 8,
                    flexShrink: 0
                }}>
                    <Icon
                        className="maestroLogoFromMaestroBanner"
                        icon="maestroBadgeImage"
                        size={72}
                        style={{
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    />
                </div>
                <div className="pendingCampaignContainer">
                    {renderMaestroBadgeLine()}
                    <div className="maestroTitleInnerContainer" style={sty.maestroTitleInnerContainer}>
                        <div className="maestroMainContentTitle" style={Sty.text16bold}>
                            Receive daily scorecards when your campaign goes live.
                        </div>
                        <div className="maestroMainContentSubtitle" style={Sty.text16grey}>
                            Maestro will analyze your campaign performance and provide optimization recommendations once your campaign is running.
                        </div>
                    </div>
                    {renderUnlockReportButton()}
                </div>
            </div>
        );
    };

    /**
     * Renders the main banner content based on campaign status
     */
    const renderBannerContent = () => {
        if (isCampaignNotRunning) {
            return renderCampaignNotRunningContent();
        }

        return (
            <>
                {(maestroStatus === MAESTRO_STATUS.LOADING || maestroStatus === MAESTRO_STATUS.TRAINING || isError) &&
                    <div
                        className="maestroLoadingTrainingContainer"
                        style={sty.maestroLoadingTrainingContainer}
                    >
                        {renderSideImage()}
                        {renderStatusMessage()}
                    </div>
                }
                {!isError && renderMaestroMainContent()}
            </>
        );
    };

    // NOTE: render()
    return (
        <Banner
            color="maestro"
            className={clsx('maestroBannerComponent', className)}
            style={styx(
                sty.maestroBannerComponent,
                { alignItems: maestroStatus === MAESTRO_STATUS.READY ? 'flex-start' : 'center' },
                style
            )}
        >
            {renderBannerContent()}
            {renderScoreCardModal()}
        </Banner>
    );
};

const sty: Record<string, CSSProperties> = {
    maestroBannerComponent: {
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 8,
        minWidth: 0,
        alignSelf: 'flex-start',
        width: '100%',
        flexShrink: 0,
    },
    maestroAiTag: {
        width: 98,
    },
    maestroBody: {
        display: 'flex',
        minWidth: 0,
    },
    maestroSideContainer: {
        padding: 16,
        maxWidth: 134,
        border: Sty.border,
        borderRadius: 12,
        width: '100%',
        overflow: 'hidden',
        flexShrink: 0,
    },
    maestroLogo: {
        width: 104,
    },
    maestroMainContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minWidth: 0,
    },
    loadingBar: {
        width: '100%',
        height: 16,
        backgroundColor: 'rgba(0,0,0,0.1)',
        borderRadius: 100,
        maxWidth: 140,
        flexShrink: 0,
    },
    loadingBarContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
    },
    gradeContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        textAlign: 'center',
        overflow: 'hidden',
        maxWidth: 134,
        minWidth: 120,
        border: Sty.border,
        borderRadius: 12,
        paddingTop: 24,
        paddingBottom: 24,
        paddingLeft: 8,
        paddingRight: 8,
        flexShrink: 0,
    },
    grade: {
        fontSize: 40,
        fontWeight: 600,
        marginBottom: -12,
    },
    gradeSubtitle: {
        fontSize: 12,
        color: Colors.greyDark,
    },
    maestroChart: {
        width: '100%',
        marginTop: 16,
        paddingLeft: 4,
        paddingRight: 4,
    },
    maestroMainContentTitle: {
    },
    maestroMainContentReady: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        minWidth: 0,
    },
    maestroTitleContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        overflow: 'hidden',
    },
    maestroTitleContainer_mobile: {
        flexDirection: 'column',
    },

    sectionHeaderTitle: {
        fontSize: 16,
        fontWeight: 500,
        marginTop: 16,
    },

    maestroInsightsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    insightsRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        overflowX: 'scroll',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        padding: 16,
        borderRadius: 16,
        boxShadow: 'inset 0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
    },
    maestroInsight: {
        padding: 12,
        border: Sty.border,
        borderRadius: 12,
        maxWidth: 240,
        width: '100%',
        flexShrink: 0,
    },
    maestroLogoInsight: {
        width: 22,
    },
    maestroInsightHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        color: Colors.greyDark,
        marginLeft: -4,
        fontSize: 12,
    },

    maestroRecommendedActionsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },

    maestroRecommendedAction: {
        fontSize: 12,
    },
    maestroRecommendedActionsRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        overflowX: 'scroll',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
    },

    donutStyle: {
        width: '134px',
        height: '67px',
        background: 'conic-gradient(#cddc39 0deg, #ffeb3b 100deg, #ff9800 180deg)',
        borderRadius: '134px 134px 0 0',
        position: 'relative',
        overflow: 'hidden',
    },
    innerCircleStyle: {
        position: 'absolute',
        width: '90px',
        height: '90px',
        backgroundColor: 'white',
        borderRadius: '50%',
        top: '22px',
        left: '22px',
    },
    maestroTagTitleContainer: {
        display: 'flex',
        gap: 16,
        flexDirection: 'column'
    },
    prosAndConsContainer: {
        marginLeft: 24,
    },
    maestroGenericContentText: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    dontWorryMessage: {
        color: Colors.purple,
        fontStyle: 'italic',
        fontSize: 14,
        maxWidth: 320,
    },
    errorContainer: {
        width: '100%',
    },
    maestroLoadingTrainingContainer: {
        display: 'flex',
        gap: 16,
        alignItems: 'center',
        width: '100%',
    },
    maestroTitleInnerContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    maestroStarsContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        alignItems: 'center',
        justifyContent: 'center',
    },
    maestroStarScore: {
        width: 32,
    },
    followUpQuestionsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginTop: 8,
    },
    followUpQuestionsList: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        flexWrap: 'wrap',
    },
    followUpQuestionButton: {
        fontSize: 14,
        padding: '6px 12px',
        borderRadius: 100,
        whiteSpace: 'nowrap',
        backgroundColor: 'rgba(136, 0, 255, 0.05)',
        borderColor: 'rgba(136, 0, 255, 0.3)',
        color: Colors.purple,
    },
    scoreCardModalContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        padding: 16,
    },
    scoreCardModalHeader: {
        display: 'flex',
        flexDirection: 'row',
        gap: 24,
        alignItems: 'flex-start',
        borderBottom: `1px solid ${Colors.greyLight}`,
        paddingBottom: 20,
    },
    scoreCardModalHeader_mobile: {
        flexDirection: 'column',
    },
    scoreCardStarsContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: 16,
        border: Sty.border,
        borderRadius: 12,
        minWidth: 150,
    },
    scoreCardTitleContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    scoreCardDetailsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
    },
}

export default MaestroBanner;
